import { Button } from "../../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import useAuth from "../../../hooks/useAuth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Import, ListVideo, Sparkles, Store, ArrowRight, FolderOpen, FolderInput, ChevronDown, PenLine, Filter, Search,
  MonitorPlay, LayoutGrid, List,
} from "lucide-react";
import React, { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  solid /* regular */,
} from "@fortawesome/fontawesome-svg-core/import.macro";
import { Skeleton } from "../../../components/ui/skeleton";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { WSContext } from "../../../App";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { config } from "../../../Environment";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "../../../components/ui/tooltip";
import LoggerContext from "../../../context/LoggerProvider";
import SingleActivity from "./singleActivity";
import AiAuthoringWizardDialog from "./aiAuthoringWizard";
import ContinueStrip from "./ContinueStrip";
import ActivityListView from "./ActivityListView";
import { cn } from "../../../lib/utils";
import { Input } from "../../../components/ui/input";
import FolderSidebar from "./FolderSidebar";
import { folderApi, metadataApi, UserFolder } from "../../../api/activityOrganization";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import {AUTHOR_ROLE, SYSADMIN_ROLE} from "../../../lib/roles";
import { useDirection } from "../../../hooks/useDirection";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { Badge } from "../../../components/ui/badge";
import { useZoneSidebar } from "../../../context/ZoneSidebarContext";

const MyActivities = ({
  // prescriptions,
  setContextPrescriptions,
  viewPrescription,
  renamePrescription,
  deletePrescription,
  copyPrescriptionAsExample,
  sharePrescription,
  managePermissions,
  expertShareWorkflow,
  exportPrescription,
  runPrescription,
  runPrescriptionById,
  refreshLists,
  isMobile,
}: any) => {
  const { auth, user } = useAuth();
  const { isRTL } = useDirection();
  const { portalContainer, collapsed: zoneSidebarCollapsed } = useZoneSidebar();

  // const [unfinishedProcessModal, setUnfinishedProcessModal] = useState<{
  //   isOpen: boolean;
  //   prescription: any;
  // }>({ isOpen: false, prescription: null });
  const [aiAuthoringWizardOpen, setAIAuthoringWizardOpen] = useState(false);
  const [wizardPrefillData, setWizardPrefillData] = useState<any>(null);
  const [wizardAutoOpen, setWizardAutoOpen] = useState(false);
  const [prescriptions, setPrescriptions] = useState<any[]>([]);
  const [search, setSearch] = useState(() => {
    // Restore search from sessionStorage on mount
    const savedSearch = sessionStorage.getItem("activitiesSearch");
    return savedSearch || "";
  });
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(() => {
    // Restore page from localStorage on mount
    const savedPage = localStorage.getItem("activitiesCurrentPage");
    return savedPage ? parseInt(savedPage, 10) : 1;
  });
  const [limit, setLimit] = useState(() => {
    // Restore limit from localStorage on mount
    const savedLimit = localStorage.getItem("activitiesPerPage");
    return savedLimit ? parseInt(savedLimit, 10) : 8;
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortMode, setSortMode] = useState<string>(() => {
    // Load sort preference from localStorage on mount
    const savedSortMode = localStorage.getItem("activitySortMode");
    return savedSortMode || "recentlyModified";
  });
  const [viewMode, setViewMode] = useState<"grid" | "list">(() => {
    const saved = localStorage.getItem("activitiesViewMode");
    return (saved === "list" ? "list" : "grid");
  });
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Cache sidebar counts from the "all" view so they persist when an ownership filter is active
  const sidebarCountsRef = React.useRef<{
    total: number | null;
    created: number | null;
    library: number | null;
    favorites: number | null;
    inProgress: number | null;
    unorganized: number | null;
  }>({ total: null, created: null, library: null, favorites: null, inProgress: null, unorganized: null });

  // Folder and metadata state
  const [folders, setFolders] = useState<UserFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(() => {
    // Restore folder context from sessionStorage on mount
    const savedFolder = sessionStorage.getItem("selectedActivityFolder");
    return savedFolder || null;
  });
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [showFolderSidebar, setShowFolderSidebar] = useState(false);
  // Collection state
  const [collections, setCollections] = useState<any[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [sharedWorkflows, setSharedWorkflows] = useState<any[]>([]);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();
  const [searchParams] = useSearchParams();

  const { listRefresh, setListRefresh } = useContext(WSContext);
  const { Logger } = useContext(LoggerContext);

  // Track initial mount to prevent unnecessary page resets
  const isInitialMount = React.useRef(true);
  const prevSearch = React.useRef(search);
  const prevFolder = React.useRef(selectedFolder);
  const hasAutoRedirected = React.useRef(false); // Track if we've already done auto-redirect

  // Read wizard data from URL parameters (marketing site integration)
  useEffect(() => {
    const wizardDataParam = searchParams.get('wizard_data');
    const autoOpenParam = searchParams.get('auto_open');

    if (wizardDataParam) {
      try {
        const parsed = JSON.parse(decodeURIComponent(wizardDataParam));

        // Store in sessionStorage to persist through auth flow
        sessionStorage.setItem('clary_wizard_prefill', JSON.stringify(parsed));
        sessionStorage.setItem('clary_wizard_auto_open', String(autoOpenParam === 'true'));

        setWizardPrefillData(parsed);
        setWizardAutoOpen(autoOpenParam === 'true');

        // Auto-open wizard if requested
        if (autoOpenParam === 'true') {
          setAIAuthoringWizardOpen(true);
        }

        // Clean URL by removing the params
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);

        Logger.info('[Wizard Integration] Loaded wizard data from marketing site', { parsed });
      } catch (err) {
        Logger.error('[Wizard Integration] Failed to parse wizard data', err);
      }
    } else {
      // Check sessionStorage for persisted data (e.g., after auth redirect)
      const storedData = sessionStorage.getItem('clary_wizard_prefill');
      const storedAutoOpen = sessionStorage.getItem('clary_wizard_auto_open');

      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          setWizardPrefillData(parsed);
          setWizardAutoOpen(storedAutoOpen === 'true');

          // Auto-open wizard if requested
          if (storedAutoOpen === 'true') {
            setAIAuthoringWizardOpen(true);
          }

          Logger.info('[Wizard Integration] Restored wizard data from sessionStorage', { parsed });
        } catch (err) {
          Logger.error('[Wizard Integration] Failed to parse stored wizard data', err);
        }
      }
    }
  }, [searchParams, Logger]);

  useEffect(() => {
    // Only reset page if search actually changed (not on initial mount)
    if (isInitialMount.current || search === prevSearch.current) {
      prevSearch.current = search;
      return;
    }

    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    prevSearch.current = search;
    return () => clearTimeout(handler);
  }, [search]);

  // Reset page when changing folders (not on initial mount)
  useEffect(() => {
    if (isInitialMount.current || selectedFolder === prevFolder.current) {
      prevFolder.current = selectedFolder;
      isInitialMount.current = false;
      return;
    }
    setPage(1);
    prevFolder.current = selectedFolder;
  }, [selectedFolder]);

  // Save page to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("activitiesCurrentPage", page.toString());
  }, [page]);

  // Save limit to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("activitiesPerPage", limit.toString());
  }, [limit]);

  // Save search to sessionStorage whenever it changes
  useEffect(() => {
    if (search) {
      sessionStorage.setItem("activitiesSearch", search);
    } else {
      sessionStorage.removeItem("activitiesSearch");
    }
  }, [search]);

  // Save selectedFolder to sessionStorage whenever it changes
  useEffect(() => {
    if (selectedFolder) {
      sessionStorage.setItem("selectedActivityFolder", selectedFolder);
    } else {
      sessionStorage.removeItem("selectedActivityFolder");
    }
  }, [selectedFolder]);

  useEffect(() => {
    setContextPrescriptions(prescriptions);
  }, [prescriptions]);

  // Function to update sort mode and save preference to localStorage
  const handleSortModeChange = (newSortMode: string) => {
    setSortMode(newSortMode);
    setPage(1); // Reset to first page when changing sort
    // Save preference to localStorage
    localStorage.setItem("activitySortMode", newSortMode);
  };

  // Function to update items per page
  const handleLimitChange = (newLimit: string) => {
    const limitNumber = parseInt(newLimit, 10);
    setLimit(limitNumber);
    setPage(1); // Reset to first page when changing items per page
  };

  // Function to toggle view mode
  const handleViewModeChange = (mode: "grid" | "list") => {
    setViewMode(mode);
    localStorage.setItem("activitiesViewMode", mode);
  };

  // Fetch folders
  useEffect(() => {
    if (!auth?.email) return;

    const fetchFolders = async () => {
      try {
        setFoldersLoading(true);
        const fetchedFolders = await folderApi.getFolders(retryFetch);
        setFolders(fetchedFolders);

        // Verify that the restored selectedFolder still exists
        // Special/virtual folders are always valid
        const virtualFolders = ["favorites", "in-progress", "unorganized", "created", "library"];
        if (selectedFolder && !virtualFolders.includes(selectedFolder) && !selectedFolder.startsWith("collection-") && !fetchedFolders.find(f => f._id === selectedFolder)) {
          // Folder was deleted, clear the selection
          setSelectedFolder(null);
        }
      } catch (err) {
        Logger.error("Failed to load folders", err);
        toast.error("Failed to load folders");
      } finally {
        setFoldersLoading(false);
      }
    };

    fetchFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.email]);

  // Fetch collections and shared workflows
  useEffect(() => {
    if (!auth?.email) return;

    const fetchSharedWorkflows = async () => {
      try {
        setCollectionsLoading(true);
        const response = await retryFetch(
          `${config.API_BASE}/api/user/shared-workflows`
        );
        const result = await response.json();

        // Handle both array format and object format { collections: [], workflows: [] }
        const collectionsData = Array.isArray(result)
          ? result
          : (result?.collections || []);

        setCollections(collectionsData);

        // Flatten all workflows from all collections
        const allSharedWorkflows = collectionsData.flatMap((collection: any) =>
          (collection.workflows || []).map((wf: any) => ({
            ...wf,
            collectionId: collection._id,
            collectionName: collection.name,
          }))
        );
        setSharedWorkflows(allSharedWorkflows);

        Logger.info(`Loaded ${collectionsData.length} collections with shared workflows`);
      } catch (err) {
        Logger.error("Failed to load shared workflows", err);
        // Don't show error toast - just log it, as this is not critical
      } finally {
        setCollectionsLoading(false);
      }
    };

    fetchSharedWorkflows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.email]);

  // Set default view for new users who belong to collections (only once on initial load)
  useEffect(() => {
    // Only run this once when both data sources have loaded
    if (loading || collectionsLoading || foldersLoading) return;

    // Only auto-redirect once, and only if user hasn't manually selected anything
    if (hasAutoRedirected.current) return;

    // Check if user has no personal activities and no selection has been made yet
    if (total === 0 && selectedFolder === null) {
      // Check if user has collections with shared workflows
      const collectionWithWorkflows = collections.find(c => c.workflowCount > 0);

      if (collectionWithWorkflows) {
        // Set the default view to the first collection with workflows
        setSelectedFolder(`collection-${collectionWithWorkflows._id}`);
        hasAutoRedirected.current = true; // Mark that we've done the auto-redirect
        Logger.info(`Setting default view to collection: ${collectionWithWorkflows.name}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, collections, collectionsLoading, loading, foldersLoading]);

  useEffect(() => {
    if (!auth?.email) return;

    const fetchPrescriptions = async () => {
      try {
        setLoading(true);
        // Server-paginated views: "All Activities", "Created by Me", "My Library"
        // Client-paginated views: user folders, favorites, in-progress, etc.
        const isServerPaginated = !selectedFolder || selectedFolder === "created" || selectedFolder === "library";
        const useLimit = (isServerPaginated && !isMobile) ? limit.toString() : "1000";

        // Read page from localStorage as fallback to ensure correct page on mount
        const savedPage = localStorage.getItem("activitiesCurrentPage");
        const currentPage = savedPage ? parseInt(savedPage, 10) : page;
        const usePage = (isServerPaginated && !isMobile) ? currentPage.toString() : "1";

        // Map virtual folder to ownershipFilter
        const ownershipFilter = selectedFolder === "created" ? "created"
          : selectedFolder === "library" ? "library"
          : "all";

        const query = new URLSearchParams({
          search: debouncedSearch,
          page: usePage,
          limit: useLimit,
          sortBy: sortMode,
          ownershipFilter,
        }).toString();

        const response = await retryFetch(
          `${config.API_BASE}/api/prescriptions/user/${auth.email}?${query}`
        );

        const result = await response.json();

        setPrescriptions(result.data || []);
        setTotal(result.total || 0);

        // Update sidebar counts from server response (always accurate regardless of pagination)
        if (ownershipFilter === "all") {
          sidebarCountsRef.current.total = result.total || 0;
          if (result.createdTotal != null) sidebarCountsRef.current.created = result.createdTotal;
          if (result.libraryTotal != null) sidebarCountsRef.current.library = result.libraryTotal;
          if (result.inProgressTotal != null) sidebarCountsRef.current.inProgress = result.inProgressTotal;
        }
      } catch (err) {
        Logger.error(
          `Failed to load prescriptions for user ${auth.email}`,
          err
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPrescriptions();
  }, [auth?.email, debouncedSearch, page, selectedFolder, sortMode, limit, isMobile]);

  // Refetch when refreshLists is called from parent
  const prevListRefresh = React.useRef(listRefresh);

  useEffect(() => {
    // Trigger refetch when listRefresh changes from false to true or when refreshLists is called
    if (listRefresh && listRefresh !== prevListRefresh.current) {
      if (!auth?.email) return;

      const refetchPrescriptions = async () => {
        try {
          setLoading(true);
          const isServerPaginated = !selectedFolder || selectedFolder === "created" || selectedFolder === "library";
          const useLimit = (isServerPaginated && !isMobile) ? limit.toString() : "1000";

          // Read page from localStorage as fallback to ensure correct page
          const savedPage = localStorage.getItem("activitiesCurrentPage");
          const currentPage = savedPage ? parseInt(savedPage, 10) : page;
          const usePage = (isServerPaginated && !isMobile) ? currentPage.toString() : "1";

          const ownershipFilter = selectedFolder === "created" ? "created"
            : selectedFolder === "library" ? "library"
            : "all";

          const query = new URLSearchParams({
            search: debouncedSearch,
            page: usePage,
            limit: useLimit,
            sortBy: sortMode,
            ownershipFilter,
          }).toString();

          const response = await retryFetch(
            `${config.API_BASE}/api/prescriptions/user/${auth.email}?${query}`
          );

          const result = await response.json();

          setPrescriptions(result.data || []);
          setTotal(result.total || 0);

          // Update sidebar counts from server response
          if (ownershipFilter === "all") {
            sidebarCountsRef.current.total = result.total || 0;
            if (result.createdTotal != null) sidebarCountsRef.current.created = result.createdTotal;
            if (result.libraryTotal != null) sidebarCountsRef.current.library = result.libraryTotal;
            if (result.inProgressTotal != null) sidebarCountsRef.current.inProgress = result.inProgressTotal;
          }
        } catch (err) {
          Logger.error(
            `Failed to reload prescriptions for user ${auth.email}`,
            err
          );
        } finally {
          setLoading(false);
        }
      };

      refetchPrescriptions();
    }
    prevListRefresh.current = listRefresh;
  }, [listRefresh, auth?.email, debouncedSearch, page, selectedFolder, sortMode, limit, retryFetch, Logger, isMobile]);

  const newPrescription = () => {
    sessionStorage.removeItem("prescriptionID");
    sessionStorage.removeItem("draftprescription");
    navigate("/design");
  };

  const editPrescription = (idxOrId: any) => {
    // Support both index (for backwards compatibility) and direct prescription ID
    const prescriptionId = typeof idxOrId === 'string'
      ? idxOrId
      : prescriptions[idxOrId]._id;
    sessionStorage.setItem("prescriptionID", prescriptionId);
    sessionStorage.removeItem("draftprescription");
    navigate(`/design?wid=${prescriptionId}`);
  };

  const handleImportPrescription = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async function (e: any) {
        const content = e.target.result;
        try {
          const importData = JSON.parse(content);

          let newResponse = await retryFetch(
            config.API_BASE + "/api/prescriptions/import",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              credentials: "include",
              body: JSON.stringify(importData),
            }
          );

          let data = await newResponse.json();

          if (!data.name) throw new Error("");
          let newName = data.name;

          toast.success("Prescription was successfully imported as " + newName);

          setListRefresh(true);
        } catch (error) {
          toast.error("Error importing prescription", {
            className: "!bg-red-500 !text-white",
          });

          console.error("Error parsing JSON:", error);
        }
      };
      reader.readAsText(file);
    }
  };

  // const handleContinueProcess = () => {
  //   const latestProcess = unfinishedProcessModal.prescription?.processes?.[0];
  //   if (latestProcess) {
  //     console.log(`Navigate to /process/${latestProcess.id}`);
  //   }
  //   setUnfinishedProcessModal({ isOpen: false, prescription: null });
  // };

  // Folder management handlers
  const handleCreateFolder = async (name: string, color: string) => {
    try {
      const newFolder = await folderApi.createFolder(name, color, undefined, retryFetch);
      setFolders([...folders, newFolder]);
      toast.success("Folder created successfully");
    } catch (err: any) {
      Logger.error("Failed to create folder", err);
      toast.error(err.message || "Failed to create folder");
    }
  };

  const handleUpdateFolder = async (
    folderId: string,
    name: string,
    color: string
  ) => {
    try {
      const updatedFolder = await folderApi.updateFolder(folderId, {
        name,
        color,
      }, retryFetch);
      setFolders(
        folders.map((f) => (f._id === folderId ? updatedFolder : f))
      );
      toast.success("Folder updated successfully");
    } catch (err: any) {
      Logger.error("Failed to update folder", err);
      toast.error(err.message || "Failed to update folder");
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    try {
      await folderApi.deleteFolder(folderId, retryFetch);
      setFolders(folders.filter((f) => f._id !== folderId));
      if (selectedFolder === folderId) {
        setSelectedFolder(null);
      }
      toast.success("Folder deleted successfully");
      // Refresh prescriptions to update folder associations
      setListRefresh(true);
    } catch (err) {
      Logger.error("Failed to delete folder", err);
      toast.error("Failed to delete folder");
    }
  };

  // Metadata handlers
  const handleMoveToFolder = async (
    prescriptionId: string,
    folderId: string | null
  ) => {
    // Optimistic update
    const prevPrescriptions = [...prescriptions];
    setPrescriptions(
      prescriptions.map((p) =>
        p._id === prescriptionId
          ? {
              ...p,
              metadata: {
                ...p.metadata,
                folderId,
                folderName: folders.find((f) => f._id === folderId)?.name,
                folderColor: folders.find((f) => f._id === folderId)?.color,
              },
            }
          : p
      )
    );
    toast.success(
      folderId ? "Activity moved to folder" : "Activity removed from folder"
    );

    try {
      await metadataApi.moveToFolder(prescriptionId, folderId, retryFetch);

      // Re-fetch folders to get updated counts
      try {
        const fetchedFolders = await folderApi.getFolders(retryFetch);
        setFolders(fetchedFolders);
      } catch (err) {
        Logger.error("Failed to refresh folder counts", err);
      }
    } catch (err) {
      // Rollback
      setPrescriptions(prevPrescriptions);
      Logger.error("Failed to move activity", err);
      toast.error("Failed to move activity");
    }
  };

  const handleToggleFavorite = async (prescriptionId: string) => {
    // Optimistic update
    const previousIsFavorite = prescriptions.find(p => p._id === prescriptionId)?.metadata?.isFavorite;
    setPrescriptions(prev =>
      prev.map((p) =>
        p._id === prescriptionId
          ? { ...p, metadata: { ...p.metadata, isFavorite: !p.metadata?.isFavorite } }
          : p
      )
    );

    try {
      const updatedMetadata = await metadataApi.toggleFavorite(prescriptionId, retryFetch);
      // Sync with server response
      setPrescriptions(prev =>
        prev.map((p) =>
          p._id === prescriptionId
            ? { ...p, metadata: updatedMetadata }
            : p
        )
      );
      toast.success(
        updatedMetadata.isFavorite
          ? "Added to favorites"
          : "Removed from favorites"
      );
    } catch (err: any) {
      // Revert on error
      setPrescriptions(prev =>
        prev.map((p) =>
          p._id === prescriptionId
            ? { ...p, metadata: { ...p.metadata, isFavorite: previousIsFavorite } }
            : p
        )
      );
      Logger.error("Failed to toggle favorite", err);
      toast.error(err.message || "Failed to toggle favorite");
      console.error("Toggle favorite error:", err);
    }
  };

  // Create a wrapper function that triggers both context refresh and local refetch
  const handleRefreshAfterOperation = () => {
    if (refreshLists) {
      refreshLists();
    }
    // Also trigger local refetch by setting listRefresh
    setListRefresh(true);
  };

  function renderActivitiesList() {
    if (Array.isArray(paginatedPrescriptions) && paginatedPrescriptions.length > 0) {
      // List view (desktop only)
      if (viewMode === "list" && !isMobile) {
        return (
          <ActivityListView
            prescriptions={paginatedPrescriptions}
            editPrescription={editPrescription}
            viewPrescription={viewPrescription}
            renamePrescription={renamePrescription}
            deletePrescription={deletePrescription}
            copyPrescriptionAsExample={copyPrescriptionAsExample}
            sharePrescription={sharePrescription}
            managePermissions={managePermissions}
            expertShareWorkflow={expertShareWorkflow}
            exportPrescription={exportPrescription}
            onMoveToFolder={handleMoveToFolder}
            onToggleFavorite={handleToggleFavorite}
            folders={folders}
            refreshLists={handleRefreshAfterOperation}
            isMobile={isMobile}
          />
        );
      }

      // Grid view (default)
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
          {paginatedPrescriptions.map((prescription: any, index: any) => (
            <SingleActivity
              key={prescription._id}
              index={index}
              prescription={prescription}
              prescriptions={prescriptions}
              setPrescriptions={setPrescriptions}
              editPrescription={editPrescription}
              viewPrescription={viewPrescription}
              renamePrescription={renamePrescription}
              deletePrescription={deletePrescription}
              copyPrescriptionAsExample={copyPrescriptionAsExample}
              sharePrescription={sharePrescription}
              managePermissions={managePermissions}
              expertShareWorkflow={expertShareWorkflow}
              exportPrescription={exportPrescription}
              runPrescription={runPrescription}
              refreshLists={handleRefreshAfterOperation}
              isMobile={isMobile}
              metadata={prescription.metadata}
              folders={folders}
              onMoveToFolder={handleMoveToFolder}
              onToggleFavorite={handleToggleFavorite}
            />
          ))}
        </div>
      );
    }
    return <></>;
  }

  function renderActivitiesListSkeleton() {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
        {Array(8)
          .fill(0)
          .map((_, arrIdx) => {
            return (
              <div key={arrIdx} className="bg-white rounded-2xl border border-gray-100 p-5 animate-pulse">
                <Skeleton className="h-5 w-3/4 rounded-md bg-gray-100 mb-3" />
                <Skeleton className="h-4 w-1/2 rounded-md bg-gray-100 mb-2" />
                <Skeleton className="h-4 w-2/3 rounded-md bg-gray-50 mb-5" />
                <div className="flex gap-2 pt-3 border-t border-gray-50">
                  <Skeleton className="h-8 w-20 rounded-lg bg-gray-100" />
                  <Skeleton className="h-8 w-16 rounded-lg bg-gray-50" />
                </div>
              </div>
            );
          })}
      </div>
    );
  }

  function renderActivitiesListEmpty() {
    if (Array.isArray(paginatedPrescriptions) && paginatedPrescriptions.length === 0) {
      // Folder-specific empty state
      if (selectedFolder) {
        return (
          <div className="flex flex-col items-center justify-center py-8 text-center min-h-[300px]">
            <div className="relative mb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center shadow-sm">
                <FolderOpen className="h-7 w-7 text-gray-400" />
              </div>
              <div className="absolute -top-1 -end-1 w-4 h-4 rounded-full bg-primary/10 flex items-center justify-center">
                <FolderInput className="h-2 w-2 text-primary" />
              </div>
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1.5">
              {t("no_activities_in_folder")}
            </h3>
            <p className="text-sm text-muted-foreground max-w-sm mb-5 leading-relaxed">
              {t("folder_empty_description")}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedFolder(null)}
              className="gap-2"
            >
              <MonitorPlay className="h-3.5 w-3.5" />
              {t("view_all_activities")}
            </Button>
          </div>
        );
      }

      // Full onboarding empty state
      const isAuthor = user.roles?.includes(AUTHOR_ROLE);
      return (
        <div className="flex flex-col items-center justify-center py-8 text-center min-h-[400px]">
          {/* Hero Icon */}
          <div className="relative mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/8 to-primary/5 flex items-center justify-center shadow-sm">
              <MonitorPlay className="h-8 w-8 text-primary" />
            </div>
            <div className="absolute -top-1 -end-1 w-5 h-5 rounded-full bg-violet-100 flex items-center justify-center">
              <Sparkles className="h-2.5 w-2.5 text-violet-500" />
            </div>
            <div className="absolute -bottom-1 -start-1 w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center">
              <div className="w-1.5 h-1.5 rounded-full bg-primary/60" />
            </div>
          </div>

          {/* Title & Description */}
          <h3 className="text-lg font-semibold text-gray-900 mb-1.5">
            {t("activities_empty_title")}
          </h3>
          <p className="text-sm text-muted-foreground max-w-md mb-6 leading-relaxed">
            {t("activities_empty_description")}
          </p>

          {/* Action Cards */}
          <div className="grid gap-2.5 w-full max-w-md">
            {/* Experiment with AI — available to all users */}
            <button
              onClick={() => setAIAuthoringWizardOpen(true)}
              className="flex items-center gap-3 p-3 rounded-xl border border-violet-100 bg-gradient-to-r rtl:bg-gradient-to-l from-violet-50/50 to-white hover:from-violet-50 hover:to-violet-50/50 hover:border-violet-200 transition-all text-start group"
            >
              <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0 group-hover:bg-violet-200 transition-colors">
                <Sparkles className="h-4 w-4 text-violet-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-gray-900">
                  {t("try_quick_ai_activity")}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {t("try_quick_ai_activity_description")}
                </p>
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-violet-500 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 rtl:rotate-180 transition-all" />
            </button>

            {/* Browse Marketplace */}
            <button
              onClick={() => navigate("/marketplace")}
              className="flex items-center gap-3 p-3 rounded-xl border border-primary/10 bg-gradient-to-r rtl:bg-gradient-to-l from-primary/5/50 to-white hover:from-primary/5 hover:to-primary/5/50 hover:border-primary/20 transition-all text-start group"
            >
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                <Store className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-gray-900">
                  {t("browse_marketplace")}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {t("browse_marketplace_description")}
                </p>
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-primary group-hover:translate-x-1 rtl:group-hover:-translate-x-1 rtl:rotate-180 transition-all" />
            </button>

            {/* Import from File (authors only) */}
            {isAuthor && (
              <button
                onClick={() => document.getElementById("importPrescription")?.click()}
                className="flex items-center gap-3 p-3 rounded-xl border border-emerald-100 bg-gradient-to-r rtl:bg-gradient-to-l from-emerald-50/50 to-white hover:from-emerald-50 hover:to-emerald-50/50 hover:border-emerald-200 transition-all text-start group"
              >
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-200 transition-colors">
                  <Import className="h-4 w-4 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900">
                    {t("import_from_file")}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    {t("import_from_file_description")}
                  </p>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-emerald-500 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 rtl:rotate-180 transition-all" />
              </button>
            )}
          </div>

          {/* Tips Section */}
          <div className="mt-6 pt-4 border-t border-gray-100 w-full max-w-md">
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-gray-600">{t("tip")}:</span>{" "}
              {t("activities_tip")}
            </p>
          </div>
        </div>
      );
    }
    return <></>;
  }


  const getPageNumbers = () => {
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (page >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(1, "...", page - 1, page, page + 1, "...", totalPages);
      }
    }
    return pages;
  };

  // Sidebar counts — sourced from server-provided totals (cached in sidebarCountsRef by fetch callbacks).
  // These are always accurate because the backend computes them across the full dataset before pagination.
  // When an ownership filter is active, the server counts are from the "all" view (last fetched).
  const cached = sidebarCountsRef.current;
  const totalCount = cached.total ?? total;
  const createdCount = cached.created;
  const libraryCount = cached.library;
  const inProgressCount = cached.inProgress ?? 0;
  // Favorites and unorganized counts are not yet server-provided, compute from current data
  const favoritesCount = prescriptions.filter((p) => p.metadata?.isFavorite).length;
  const unorganizedCount = prescriptions.filter((p) => !p.metadata?.folderId).length;

  // Detect non-default filter/sort settings for the Filters badge
  const defaultLimit = isMobile ? 8 : 12;
  const hasActiveFilters = sortMode !== "recentlyModified" || limit !== defaultLimit || statusFilter !== "all";

  // Client-side filtering when folder is selected
  // "created" and "library" are server-filtered, so pass through
  let filteredPrescriptions = selectedFolder
    ? selectedFolder === "created" || selectedFolder === "library"
      ? prescriptions // Server already filtered via ownershipFilter
      : selectedFolder === "favorites"
      ? prescriptions.filter((p) => p.metadata?.isFavorite)
      : selectedFolder === "unorganized"
      ? prescriptions.filter((p) => !p.metadata?.folderId)
      : selectedFolder === "in-progress"
      ? prescriptions.filter((p) =>
          Array.isArray(p.processes) && p.processes.some((proc: any) => proc.status === "RUNNING")
        )
      : selectedFolder.startsWith("collection-")
      ? sharedWorkflows.filter((wf) => `collection-${wf.collectionId}` === selectedFolder)
      : prescriptions.filter((p) => p.metadata?.folderId === selectedFolder)
    : prescriptions;

  // Apply status filter
  if (statusFilter !== "all") {
    filteredPrescriptions = filteredPrescriptions.filter((p) => {
      const hasRunning = Array.isArray(p.processes) && p.processes.some((proc: any) => proc.status === "RUNNING");
      if (statusFilter === "in_progress") return hasRunning;
      if (statusFilter === "not_started") return !hasRunning && (!p.engagementCount || p.engagementCount === 0);
      return true;
    });
  }

  // Calculate pagination based on context
  // Server-paginated: "All Activities", "Created by Me", "My Library"
  // Client-paginated: user folders, favorites, in-progress, collections, etc.
  const isServerPaginated = !selectedFolder || selectedFolder === "created" || selectedFolder === "library";
  const totalPages = isMobile ? 1 : (isServerPaginated
    ? Math.ceil(total / limit) || 1
    : Math.ceil(filteredPrescriptions.length / limit) || 1);

  // For client-paginated views, slice locally; for server-paginated, data is already paged
  const paginatedPrescriptions = isMobile
    ? filteredPrescriptions
    : (isServerPaginated
      ? filteredPrescriptions
      : filteredPrescriptions.slice((page - 1) * limit, page * limit));

  const handleWizardClose = () => {
    setAIAuthoringWizardOpen(false);
    // Ensure cleanup happens
    sessionStorage.removeItem('clary_wizard_prefill');
    sessionStorage.removeItem('clary_wizard_auto_open');
    setWizardPrefillData(null);
    setWizardAutoOpen(false);
  };

  return (
    <TooltipProvider>
      <div className="h-full flex flex-col ">
        {aiAuthoringWizardOpen && (
          <AiAuthoringWizardDialog
            open={aiAuthoringWizardOpen}
            onClose={handleWizardClose}
            runPrescriptionById={runPrescriptionById}
            refreshActivities={refreshLists}
            isMobile={isMobile}
            prefillData={wizardPrefillData}
            autoOpen={wizardAutoOpen}
          />
        )}



{/* Portal desktop FolderSidebar into ZoneSidebar */}
{portalContainer && !isMobile && createPortal(
  <FolderSidebar
    selectedFolder={selectedFolder}
    onFolderSelect={(folderId) => {
      setSelectedFolder(folderId);
    }}
    folders={folders}
    collections={collections}
    onCreateFolder={handleCreateFolder}
    onUpdateFolder={handleUpdateFolder}
    onDeleteFolder={handleDeleteFolder}
    totalCount={totalCount}
    favoritesCount={favoritesCount}
    unorganizedCount={unorganizedCount}
    inProgressCount={inProgressCount}
    createdCount={createdCount ?? undefined}
    libraryCount={libraryCount ?? undefined}
    isMobile={false}
    collapsed={zoneSidebarCollapsed}
    embedded
  />,
  portalContainer,
)}

<div className="flex flex-row flex-1">
  {/* Mobile Folder Sheet */}
  {isMobile && (
    <Sheet open={showFolderSidebar} onOpenChange={setShowFolderSidebar}>
      <SheetContent side={isRTL ? "right" : "left"} className="w-[280px] p-0 pt-12">
        <FolderSidebar
            selectedFolder={selectedFolder}
            onFolderSelect={(folderId) => {
              setSelectedFolder(folderId);
              setShowFolderSidebar(false);
            }}
            folders={folders}
            collections={collections}
            onCreateFolder={handleCreateFolder}
            onUpdateFolder={handleUpdateFolder}
            onDeleteFolder={handleDeleteFolder}
            totalCount={totalCount}
            favoritesCount={favoritesCount}
            unorganizedCount={unorganizedCount}
            inProgressCount={inProgressCount}
            createdCount={createdCount ?? undefined}
            libraryCount={libraryCount ?? undefined}
            isMobile={true}
        />
      </SheetContent>
    </Sheet>
  )}

  <div className="flex flex-col flex-1 overflow-hidden px-3 sm:px-4 md:px-6">
    <div className="flex-1 overflow-auto pb-16 md:pb-0 pt-3 sm:pt-6">
      {/* Folder breadcrumb/filter indicator */}
     {/* {selectedFolder && (
          <div className="flex items-center gap-2 mb-2">
            <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedFolder(null)}
                className="text-gray-600 hover:text-gray-900"
            >
              {isRTL ? (
                <FontAwesomeIcon icon={solid("arrow-right")} className="me-2" />
              ) : (
                <FontAwesomeIcon icon={solid("arrow-left")} className="me-2" />
              )}
              All Activities
            </Button>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">
                {selectedFolder === "favorites"
                    ? "Favorites"
                    : selectedFolder === "unorganized"
                        ? "Unorganized"
                        : selectedFolder.startsWith("collection-")
                        ? collections.find((c) => `collection-${c._id}` === selectedFolder)?.name || "Collection"
                        : folders.find((f) => f._id === selectedFolder)?.name || "Folder"}
              </span>
          </div>
      )}*/}

      {/* Continue where you left off strip */}
      <ContinueStrip prescriptions={prescriptions} isMobile={isMobile} refreshLists={handleRefreshAfterOperation} />

      {/* Action bar with buttons and search */}
      <div className="flex flex-col gap-2 sm:gap-3 pb-3 border-b border-gray-100 mb-4 sm:mb-5">
        <div className="flex flex-row gap-2 items-center sm:justify-between">

          {/* Left: Search with icon */}
          <div className="relative flex-1 sm:max-w-md min-w-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder={t("search_by_name")}
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              className="pl-10 h-9 bg-gray-50/80 border border-gray-100 rounded-xl focus-visible:ring-1 focus-visible:ring-primary/40 focus-visible:border-primary/30 focus-visible:bg-white transition-all placeholder:text-gray-400"
            />
          </div>

          {/* Right: action buttons */}
          <div className="flex gap-2 items-center flex-shrink-0">

            {/* Desktop: View toggle (Grid/List) */}
            {!isMobile && (
              <div className="hidden sm:flex rounded-lg bg-muted p-0.5 w-fit">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-7 w-7 p-0 rounded-md",
                    viewMode === "grid"
                      ? "bg-background shadow-sm text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  onClick={() => handleViewModeChange("grid")}
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-7 w-7 p-0 rounded-md",
                    viewMode === "list"
                      ? "bg-background shadow-sm text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  onClick={() => handleViewModeChange("list")}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Mobile: Folder toggle (icon only) */}
            {isMobile && (
              <Button
                onClick={() => setShowFolderSidebar(true)}
                variant="outline"
                size="sm"
                className="h-9 w-9 p-0 flex-shrink-0"
              >
                <FontAwesomeIcon icon={solid("folder")} />
              </Button>
            )}

            {/* Desktop: Filters popover */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="hidden sm:flex h-9 px-3 flex-shrink-0 relative"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {t("filters", "Filters")}
                  {hasActiveFilters && (
                    <span className="absolute -top-1 -end-1 w-2.5 h-2.5 bg-primary rounded-full border-2 border-background" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-0">
                <div className="p-3 border-b">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm">{t("filters", "Filters")}</h4>
                    {hasActiveFilters && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          handleSortModeChange("recentlyModified");
                          handleLimitChange(defaultLimit.toString());
                          setStatusFilter("all");
                        }}
                      >
                        {t("clear_all", "Clear all")}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="p-3 space-y-4">
                  {/* Sort by */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">{t("sort_by", "Sort by")}</label>
                    <Select value={sortMode} onValueChange={handleSortModeChange}>
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder={t("sort_by") || "Sort by"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="recentlyModified">
                          {t("recently_modified") || "Recently Modified"}
                        </SelectItem>
                        <SelectItem value="name">
                          {t("name") || "Name"}
                        </SelectItem>
                        <SelectItem value="mostEngagements">
                          {t("most_engagements") || "Most Engagements"}
                        </SelectItem>
                        <SelectItem value="noSort">
                          {t("no_sort") || "No Sort"}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Status filter */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">{t("status_filter", "Status")}</label>
                    <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setPage(1); }}>
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("all_statuses", "All")}</SelectItem>
                        <SelectItem value="in_progress">{t("in_progress")}</SelectItem>
                        <SelectItem value="not_started">{t("not_started")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Items per page */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">{t("items_per_page", "Items per page")}</label>
                    <Select value={limit.toString()} onValueChange={handleLimitChange}>
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="8">8</SelectItem>
                        <SelectItem value="12">12</SelectItem>
                        <SelectItem value="16">16</SelectItem>
                        <SelectItem value="24">24</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Desktop: Create Activity dropdown */}
            {!isMobile && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90 text-white shadow-sm hover:shadow-primary/25 transition-all">
                    <FontAwesomeIcon icon={solid("plus")} className="me-2" />
                    {t("create_activity")}
                    <ChevronDown className="ms-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  {/* Quick AI Activity — always enabled */}
                  <DropdownMenuItem onClick={() => setAIAuthoringWizardOpen(true)} className="flex items-start gap-3 p-3 cursor-pointer">
                    <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="h-4 w-4 text-violet-600" />
                    </div>
                    <div>
                      <div className="font-medium text-sm">{t("quick_ai_activity")}</div>
                      <div className="text-xs text-muted-foreground">{t("quick_ai_activity_desc")}</div>
                    </div>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  {/* Activity Designer — disabled for non-authors */}
                  {auth.email && user.roles?.includes(AUTHOR_ROLE) ? (
                    <DropdownMenuItem onClick={() => newPrescription()} className="flex items-start gap-3 p-3 cursor-pointer">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <PenLine className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium text-sm">{t("activity_designer")}</div>
                        <div className="text-xs text-muted-foreground">{t("activity_designer_desc")}</div>
                      </div>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => navigate("/marketplace?tab=platform")} className="flex items-start gap-3 p-3 opacity-60 cursor-pointer">
                      <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <PenLine className="h-4 w-4 text-gray-400" />
                      </div>
                      <div>
                        <div className="font-medium text-sm flex items-center gap-2">
                          {t("activity_designer")}
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-purple-100 text-purple-700 border-purple-200">
                            PRO
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">{t("requires_author_subscription")}</div>
                      </div>
                    </DropdownMenuItem>
                  )}

                  {/* Import from file — SYSADMIN only */}
                  {auth.email && user.roles?.includes(SYSADMIN_ROLE) && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => (document.querySelector("#importPrescription") as HTMLElement)?.click()}
                        className="flex items-start gap-3 p-3 cursor-pointer"
                      >
                        <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <Import className="h-4 w-4 text-emerald-600" />
                        </div>
                        <div>
                          <div className="font-medium text-sm">{t("import_from_file", "Import from file")}</div>
                          <div className="text-xs text-muted-foreground">{t("import_from_file_description", "Import a JSON activity file")}</div>
                        </div>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Mobile: AI Assistance button (icon only) */}
            {isMobile && (
              <Button
                onClick={() => setAIAuthoringWizardOpen(true)}
                variant="outline"
                size="sm"
                className="h-9 w-9 p-0 flex-shrink-0"
              >
                <FontAwesomeIcon icon={solid("wand-magic-sparkles")} />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Hidden file input for import */}
      <input
        accept=".json"
        type="file"
        id="importPrescription"
        onChange={handleImportPrescription}
        className="hidden"
      />
      {/* Activities list */}
      {loading
        ? renderActivitiesListSkeleton()
        : paginatedPrescriptions?.length
        ? renderActivitiesList()
        : renderActivitiesListEmpty()}
    </div>

    {/* Pagination - Stuck at bottom (Desktop only) */}
    {!isMobile && paginatedPrescriptions?.length > 0 && (
      <div className="border-t border-gray-100 bg-white/80 backdrop-blur-sm px-3 sm:px-4 md:px-8">
        <div className="flex flex-row items-center justify-between py-4">
            {/* Showing X-Y of Z */}
            <div className="text-sm text-gray-500">
              {(() => {
                const totalItems = selectedFolder ? filteredPrescriptions.length : total;
                const from = (page - 1) * limit + 1;
                const to = Math.min(page * limit, totalItems);
                return t("showing_x_of_y", { from, to, total: totalItems });
              })()}
            </div>

            {/* Prev / Next */}
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  {t("previous")}
                </Button>
                <span className="text-sm text-muted-foreground px-2">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t("next")}
                </Button>
              </div>
            )}
        </div>
      </div>
    )}
  </div>
</div>
</div>
    </TooltipProvider>
  );
};

export default MyActivities;
